import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import MetaTags from '../../components/SEO/MetaTags';
import { pageConfigs } from '../../utils/seo';

const ProvingOutcomesFuturePT: React.FC = () => {
  return (
    <>
      <MetaTags
        title={pageConfigs.blogProvingOutcomes.title}
        description={pageConfigs.blogProvingOutcomes.description}
        keywords={pageConfigs.blogProvingOutcomes.keywords}
        canonical={pageConfigs.blogProvingOutcomes.canonical}
      />
      
      <div className="bg-white">
        {/* Hero section with title */}
        <div className="bg-darkGreen text-white pt-36 pb-8 mb-16 relative overflow-hidden">
          <div className="relative z-10">
            <div className="max-w-3xl mx-auto text-center px-4">
              <h4 className="text-emerald-400 font-medium mb-2">Blog Post</h4>
              <h1 className="text-4xl lg:text-5xl font-bold mb-4 font-productSans">
                Proving Outcomes Is the Future of PT — Are We Ready?
              </h1>
              <p className="text-emerald-100 text-lg">
                How outcome tracking is reshaping physical therapy and what it means for providers
              </p>
              <p className="text-emerald-200 text-sm mt-4">
                By Christina Rama PT, DPT • July 30, 2025
              </p>
            </div>
          </div>
        </div>

        {/* Blog content */}
        <div className="max-w-3xl mx-auto px-4 pb-16">
          {/* Back button at top */}
          <div className="mb-8">
            <Link
              to="/blogs"
              className="inline-flex items-center px-6 py-3 border border-gray-800 rounded-full text-gray-800 hover:bg-darkGreen hover:text-white transition-colors"
            >
              <ArrowLeft className="mr-2 h-4 w-4" /> Back to Blogs
            </Link>
          </div>

          {/* Blog image */}
          <div className="mb-12">
            <img
              src="/blog/blog-5.png"
              alt="Proving Outcomes Is the Future of PT — Are We Ready?"
              className="w-full rounded-lg shadow-lg"
            />
          </div>

          {/* Article content */}
          <article className="prose prose-lg max-w-none">
            <p className="text-gray-700 leading-relaxed mb-6">
              There's a shift happening in healthcare—and physical therapy is right in the middle of it.
            </p>

            <p className="text-gray-700 leading-relaxed mb-6">
              More and more, we're being asked not just to provide care, but to show how it's working. Payers, systems, and even patients are asking:
            </p>

            <blockquote className="border-l-4 border-emerald-500 pl-6 italic text-lg text-gray-600 mb-8">
              "Can we track progress in a way that tells a clear story?"
            </blockquote>

            <p className="text-gray-700 leading-relaxed mb-8">
              This isn't about questioning the quality of care we deliver—it's about making sure that quality is seen, understood, and valued.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-6 mt-12">Why Outcomes Are Becoming Essential</h2>

            <p className="text-gray-700 leading-relaxed mb-6">
              We know PT works.
            </p>

            <p className="text-gray-700 leading-relaxed mb-8">
              We reduce pain, improve function, and help people move better and live better. But as value-based care models continue to grow, having clear, measurable outcomes is becoming a bigger part of how care is evaluated, reimbursed, and improved.
            </p>

            <p className="text-gray-700 leading-relaxed mb-8">
              This doesn't mean replacing clinical judgment with metrics. It means finding ways to highlight the impact we're already making.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-6 mt-12">What That Might Look Like</h2>

            <p className="text-gray-700 leading-relaxed mb-6">
              Outcome tracking can take many forms:
            </p>

            <ul className="list-disc pl-8 text-gray-700 leading-relaxed mb-8 space-y-2">
              <li>Patient-reported outcomes</li>
              <li>Standardized tools</li>
              <li>Functional milestones</li>
              <li>Narrative progress over time</li>
            </ul>

            <p className="text-gray-700 leading-relaxed mb-8">
              What matters is that it's consistent, connected to care decisions, and easy to reference—for the therapist, the patient, and the system they're in.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-6 mt-12">The Challenge (and Opportunity)</h2>

            <p className="text-gray-700 leading-relaxed mb-8">
              Many of us are juggling full schedules, admin tasks, and documentation requirements that weren't built with outcome tracking in mind. And with no universal standard across the profession, it's easy to feel unsure about what to track—or how.
            </p>

            <p className="text-gray-700 leading-relaxed mb-6">
              But there's also opportunity here.
            </p>

            <p className="text-gray-700 leading-relaxed mb-8">
              We can shape how outcomes are defined and shared in PT. We can build systems that reflect both the art and science of what we do.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-6 mt-12">Let's Talk About What Helps</h2>

            <p className="text-gray-700 leading-relaxed mb-8">
              At Notation, we're exploring ways to support outcome tracking inside the flow of real sessions—without adding more to your plate. We believe it's possible to build documentation that supports clinical insight and shows measurable progress.
            </p>

            <p className="text-gray-700 leading-relaxed mb-6">
              If we're moving toward value-based care, what needs to change for PTs to thrive in it?
            </p>

            <p className="text-gray-700 leading-relaxed mb-6">
              This shift is happening across healthcare—how are you navigating it in your setting?
            </p>

            <p className="text-gray-700 leading-relaxed mb-8">
              We'd love to hear your thoughts
            </p>

            {/* Try Notation CTA Banner */}
            <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-2xl p-8 text-center my-12">
              <p className="text-white text-lg font-semibold mb-6">
                👉 Try Notation for your next evaluation — no strings attached.
              </p>
              <a
                href="https://notation.fownd.care/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-white text-emerald-600 px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors shadow-lg"
              >
                Try Notation
              </a>
            </div>

            {/* Back to Blogs */}
            <div className="mt-16 pt-8 border-t border-gray-200">
              <Link
                to="/blogs"
                className="inline-flex items-center px-6 py-3 border border-gray-800 rounded-full text-gray-800 hover:bg-darkGreen hover:text-white transition-colors"
              >
                <ArrowLeft className="mr-2 h-4 w-4" /> Back to all blogs
              </Link>
            </div>
          </article>
        </div>
      </div>
    </>
  );
};

export default ProvingOutcomesFuturePT; 